import { useState } from "react";
import { useLocation } from "react-router-dom";
import { fueraDeLaLeccion } from "@/lib/pantallaDeLeccion.mjs";
import { Link } from "react-router-dom";
import { CalendarClock, X } from "lucide-react";
import { aviso } from "@/lib/finDePlan.mjs";
import { useAuth } from "@/context/AuthContext";

/**
 * «Te quedan seis días». La cuenta atrás del plan, encima de todo.
 *
 * POR QUE EXISTE. El piloto del instituto da acceso completo a ciento ochenta alumnos durante
 * un mes, y cuando se acaba se acaba: la lección siguiente deja de abrirse. Sin aviso, eso se
 * vive como una avería —«a mí ayer me funcionaba»— y la conversión, que es el único momento
 * en que esto cobra, se convierte en una queja. Con aviso es una decisión que se toma en casa
 * con una semana por delante.
 *
 * SE DICE LO QUE SE CONSERVA, Y NO SÓLO LO QUE SE PIERDE. La cuenta no se cierra, ni el XP,
 * ni la estrella, ni las lecciones hechas. Un aviso que insinúa que se pierde todo asusta a
 * un chaval de catorce años y además es mentira, que es motivo de sobra.
 *
 * SE PUEDE CERRAR, PERO VUELVE MAÑANA. Un aviso que no se puede quitar acaba siendo parte del
 * paisaje y deja de leerse; uno que se quita para siempre no avisa de nada el último día. Se
 * calla lo que queda de hoy, y al día siguiente vuelve con un número menos.
 *
 * LA REGLA NO ESTÁ AQUÍ: está en `lib/finDePlan.mjs`, que dice lo mismo que
 * `app/domain/acceso.py` y se prueba sin navegador.
 */
const GUARDADO = "alpha_aviso_plan_visto";

// La marca lleva el DIA y la clase de banda. Sin la clase, cerrar la cuenta atrás el
// último día se llevaría por delante el aviso de que se acabó, que sale al siguiente y
// es el que de verdad hay que leer.

export default function AvisoDePlan() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [cerrado, setCerrado] = useState(false);

  const { avisar, acabado, dias, frase, seQueda } = aviso(user);
  if (!avisar || cerrado) return null;

  // El día en que se cerró, no un `true`: así vuelve mañana solo. Entre `try` porque un
  // navegador con el almacenamiento capado no puede dejar sin avisar a nadie.
  let visto = "";
  try {
    visto = window.localStorage.getItem(GUARDADO) || "";
  } catch {}
  const hoy = `${new Date().toISOString().slice(0, 10)}:${acabado ? "fin" : "cuenta"}`;
  if (visto === hoy) return null;

  const cierra = () => {
    try {
      window.localStorage.setItem(GUARDADO, hoy);
    } catch {}
    setCerrado(true);
  };

  return (
    <div
      role="status"
      data-testid="aviso-plan"
      data-dias={dias}
      data-acabado={acabado ? "si" : "no"}
      className={`rounded-caja bg-alpha-black text-white/90 px-4 sm:px-6 py-2.5 ${fueraDeLaLeccion(pathname)}`}
    >
      <div className="max-w-[1100px] mx-auto flex items-start gap-3">
        <CalendarClock aria-hidden="true" className="size-4 mt-0.5 shrink-0 text-alpha-red" />
        <div className="min-w-0 flex-1 text-[13px] leading-snug">
          <span className="font-semibold">{frase}</span>{" "}
          <span className="text-white/60">{seQueda}</span>{" "}
          {/* ANTES Y DESPUES NO LLEVAN AL MISMO SITIO. Mientras quedan días, lo útil es
              saber qué se puede contratar. Cuando ya pasó, lo primero no es una página de
              precios sino que no ha perdido nada, y eso lo cuenta `/fin-de-acceso`. */}
          <Link
            to={acabado ? "/fin-de-acceso" : "/precios"}
            className="underline underline-offset-2 hover:text-white transition-colors"
          >
            {acabado ? "Qué pasa ahora" : "Ver qué pasa después"}
          </Link>
        </div>
        <button
          type="button"
          onClick={cierra}
          aria-label="Cerrar el aviso por hoy"
          data-testid="cerrar-aviso-plan"
          className="shrink-0 text-white/40 hover:text-white transition-colors"
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
}
